import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DenseLayoutComponent } from '@app/_shared/components/layout/layouts/dense/dense.component';
import { AppGlobalActions } from '@app/_store/actions/global.actions';
import { selectAppDataLoading } from '@app/_store/selectors/global.selectors';
import {
  KeyboardShortcutService,
  ShortcutConfig,
} from '@core/services/keyboard-shortcuts.service';
import { NavigationService } from '@core/services/navigation.service';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
  FuseConfig,
  FuseConfigService,
  Scheme,
  Theme,
} from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Subject,
  takeUntil,
} from 'rxjs';

import { EmptyLayoutComponent } from './layouts/empty/empty.component';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EmptyLayoutComponent,
    DenseLayoutComponent,
    FuseLoadingBarComponent,
  ],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private shortcutHandler = inject(KeyboardShortcutService);
  private _activatedRoute = inject(ActivatedRoute);
  private _renderer2 = inject(Renderer2);
  private _router = inject(Router);
  private _fuseConfigService = inject(FuseConfigService);
  private _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private _fusePlatformService = inject(FusePlatformService);
  private _navigation = inject(NavigationService);
  private _document = inject(DOCUMENT);

  config!: FuseConfig;
  /**
   * A signal: the template picks the shell off it, and it is written from the
   * config and NavigationEnd subscribes below — under OnPush a plain field
   * written there would leave the old shell on screen.
   */
  readonly layout = signal<string>('');
  scheme!: Scheme;
  theme!: Theme;
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    // Set the theme and scheme based on the configuration
    combineLatest([
      this._fuseConfigService.config$,
      this._fuseMediaWatcherService.onMediaQueryChange$([
        '(prefers-color-scheme: dark)',
        '(prefers-color-scheme: light)',
      ]),
    ])
      .pipe(
        takeUntil(this._unsubscribeAll),
        map(([config, mql]) => {
          const options = {
            scheme: config.scheme,
            theme: config.theme,
          };

          // If the scheme is setItem to 'auto'...
          if (config.scheme === 'auto') {
            // Decide the scheme using the media query
            options.scheme = mql.breakpoints['(prefers-color-scheme: dark)']
              ? 'dark'
              : 'light';
          }

          return options;
        })
      )
      .subscribe((options) => {
        // Store the options
        this.scheme = options.scheme;
        this.theme = options.theme;

        // Update the scheme and theme
        this._updateScheme();
        this._updateTheme();
      });

    // Subscribe to config changes
    this._fuseConfigService.config$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config: FuseConfig) => {
        // Store the config
        this.config = config;

        // Update the layout
        this._updateLayout();
      });

    // Subscribe to NavigationEnd event
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((_event) => {
        // Update the layout
        this._updateLayout();
      });

    // Set the OS name
    this._renderer2.addClass(
      this._document.body,
      this._fusePlatformService.osName
    );

    // Set the Browser name
    this._renderer2.addClass(
      this._document.body,
      this._fusePlatformService.browserName
    );
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();

    // R65. The ⌘E registration lives in a root service, so it outlives this component unless
    // it is handed back. Leaving it behind is not a memory problem — the map is bounded, and
    // this callback only touches the store — but a stale registration is refused when the next
    // layout tries to claim the same shortcut, and then ⌘E belongs to a component that no
    // longer exists. Releasing it on destroy is what makes that impossible rather than
    // merely unlikely.
    this._setShortcutsHandlers('destroyed');
  }

  /**
   * Update the selected layout
   */
  private _updateLayout(): void {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    let layout = this.config.layout;

    const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
    if (layoutFromQueryParam) {
      layout = layoutFromQueryParam;
      if (this.config) {
        this.config.layout = layoutFromQueryParam;
      }
    }

    const paths = route.pathFromRoot;
    paths.forEach((path) => {
      if (path.routeConfig && path.routeConfig.data) {
        const { layout: routeLayout, state } = path.routeConfig.data;
        // Set the layout
        if (routeLayout) {
          layout = routeLayout;
        }

        if (state) {
          this._navigation.setNavigation(state);

          if (state === 'auth') {
            this.scheme = 'light';
          }

          this._updateScheme();
          this._setShortcutsHandlers(state);
        }
      }
    });

    this.layout.set(layout);
  }

  /**
   * Update the selected scheme
   *
   * @private
   */
  private _updateScheme(): void {
    this._fuseConfigService.updateScheme(this.scheme);
  }

  /**
   * Update the selected theme
   *
   * @private
   */
  private _updateTheme(): void {
    this._fuseConfigService.updateTheme(this.theme);
  }

  private _setShortcutsHandlers(state: string): void {
    const config: ShortcutConfig = {
      key: 'e',
      modifiers: ['primaryModifier'],
      preventDefault: true,
      callback: async () => {
        const loading = await firstValueFrom(
          this.store.select(selectAppDataLoading)
        );
        if (loading) return;
        this.store.dispatch(
          AppGlobalActions.loadAppData({ fields: null, reset: true })
        );
      },
    };

    if (state !== 'app') {
      this.shortcutHandler.unregisterShortcut(config);
      return;
    }

    this.shortcutHandler.registerShortcut(config);
  }
}
